import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/AuthContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Factory,
  FlaskConical,
  QrCode,
  CheckCircle2,
  ArrowRight,
  Tractor,
  Microscope,
  Building2,
} from 'lucide-react'

const ONBOARDING_KEY = 'agrobio_onboarding_completed'
const ONBOARDING_SEEN_KEY = 'agrobio_onboarding_seen'

const roles = [
  {
    id: 'producer',
    label: 'Produtor Rural',
    description: 'Produzo bioinsumos ou uso na lavoura',
    icon: Tractor,
  },
  {
    id: 'biofactory',
    label: 'Biofabrica',
    description: 'Fabrico bioinsumos comerciais',
    icon: Factory,
  },
  {
    id: 'researcher',
    label: 'Pesquisador',
    description: 'Pesquiso e desenvolvo novas solucoes',
    icon: Microscope,
  },
  {
    id: 'other',
    label: 'Outro',
    description: 'Distribuidor, cooperativa ou parceiro',
    icon: Building2,
  },
]

interface Step {
  id: number
  title: string
  description: string
  icon: typeof Factory
  action: string
  route: string
}

const actionSteps: Step[] = [
  {
    id: 1,
    title: 'Registre sua Biofabrica',
    description:
      'Cadastre sua unidade de producao. Com isso voce rastreia todos os lotes — do laboratorio ao campo.',
    icon: Factory,
    action: 'Registrar Biofabrica',
    route: '/app/biofactory/new',
  },
  {
    id: 2,
    title: 'Crie seu Primeiro Lote',
    description:
      'Cada lote recebe um codigo unico com QR. Registre volume, pH, concentracao e validade.',
    icon: FlaskConical,
    action: 'Criar Lote',
    route: '/app/batches',
  },
  {
    id: 3,
    title: 'Gere QR Codes de Rastreabilidade',
    description:
      'Seus lotes ganham QR codes que qualquer comprador pode escanear para verificar origem e qualidade.',
    icon: QrCode,
    action: 'Ver Rastreabilidade',
    route: '/app/traceability',
  },
]

export function OnboardingWizard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<'role' | 'overview' | 'steps'>('role')
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY)
    const seenThisSession = sessionStorage.getItem(ONBOARDING_SEEN_KEY)

    if (completed || seenThisSession) return

    // Show only for users who likely need setup (no org = new user)
    if (profile && !profile.organization_id) {
      const timer = setTimeout(() => {
        setOpen(true)
        sessionStorage.setItem(ONBOARDING_SEEN_KEY, 'true')
      }, 1200)
      return () => clearTimeout(timer)
    }
  }, [profile])

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setOpen(false)
  }

  const handleSelectRole = (roleId: string) => {
    setSelectedRole(roleId)
  }

  const handleRoleContinue = () => {
    setPhase('overview')
  }

  const handleStartSteps = () => {
    setPhase('steps')
    setCurrentStep(0)
  }

  const handleNext = () => {
    if (currentStep < actionSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleAction = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setOpen(false)
    navigate(actionSteps[currentStep].route)
  }

  // Total progress: role (1) + overview (1) + 3 steps = 5 phases
  const totalPhases = 5
  const currentPhase = phase === 'role' ? 1 : phase === 'overview' ? 2 : 3 + currentStep
  const progress = (currentPhase / totalPhases) * 100

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        {/* Phase 1: Role Selection */}
        {phase === 'role' && (
          <>
            <DialogHeader className="text-center">
              <img
                src="/logo.jpg"
                alt="AgroBio"
                width={64}
                height={64}
                className="mx-auto mb-4 h-16 w-16 rounded-full object-cover ring-2 ring-primary/20"
              />
              <DialogTitle className="text-xl flex items-center justify-center gap-2">
                Bem-vindo ao AgroBio!
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                Rastreabilidade que abre mercados.
                <br />
                <span className="text-foreground font-medium">
                  Como voce atua no setor de bioinsumos?
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              {roles.map((r) => {
                const Icon = r.icon
                const isSelected = selectedRole === r.id
                return (
                  <button
                    key={r.id}
                    onClick={() => handleSelectRole(r.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent bg-muted/50 hover:bg-muted'
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-primary/10' : 'bg-background'
                    }`}>
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>{r.label}</p>
                      <p className="text-xs text-muted-foreground">{r.description}</p>
                    </div>
                    {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </button>
                )
              })}
            </div>
            <Progress value={progress} className="h-1" />
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={handleSkip} className="sm:mr-auto">
                Pular por agora
              </Button>
              <Button onClick={handleRoleContinue} disabled={!selectedRole} className="gap-2">
                Continuar <ArrowRight className="h-4 w-4" />
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Phase 2: Overview */}
        {phase === 'overview' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <DialogTitle className="text-xl">
                3 passos para rastrear seus bioinsumos
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                Leva menos de 5 minutos. Voce pode fazer um por vez.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {actionSteps.map((s, idx) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                >
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{s.title}</p>
                  </div>
                  <s.icon className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-1" />
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={handleSkip} className="sm:mr-auto">
                Pular por agora
              </Button>
              <Button onClick={handleStartSteps} className="gap-2">
                Comecar <ArrowRight className="h-4 w-4" />
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Phase 3: Step-by-step */}
        {phase === 'steps' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                {(() => { const StepIcon = actionSteps[currentStep].icon; return <StepIcon className="h-8 w-8 text-primary" /> })()}
              </div>
              <DialogTitle className="text-xl">
                {actionSteps[currentStep].title}
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                {actionSteps[currentStep].description}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                {actionSteps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 rounded-full transition-all ${
                      idx <= currentStep ? 'w-8 bg-primary' : 'w-2 bg-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
            <Progress value={progress} className="h-1" />
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={handleSkip} className="sm:mr-auto">
                Pular por agora
              </Button>
              {currentStep < actionSteps.length - 1 ? (
                <Button onClick={handleNext} className="gap-2">
                  Proximo <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button onClick={handleAction} className="gap-2">
                  {actionSteps[currentStep].action} <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
